import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { AuthService } from '../../../../shared/libs/auth/auth.service';
import { Router } from '@angular/router';
import { URLS_GUARD } from '../../../../shared/libs/models/url-guard.enum';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { switchMap } from 'rxjs';

@Component({
	standalone: false,
	selector: 'bt-login',
	templateUrl: './login.component.html',
	styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {
	readonly #authService = inject(AuthService);
	readonly #router = inject(Router);
	readonly #destroyRef = inject(DestroyRef);

	ngOnInit(): void {
		if (this.#authService.userLoggedValue) {
			const url = this.#authService.isCompany() ? URLS_GUARD.COMPANY : URLS_GUARD.PHOTOGRAPHER;
			this.#router.navigate([url]);
		}
		this.check();
	}

	check(): void {
		// la rotta richiesta prima del login, letta ora perché authentication() la sovrascrive con /login
		const requestedRoute = localStorage.getItem('BARequestRoute');

		this.#authService
			.getJwtToken()
			.pipe(
				takeUntilDestroyed(this.#destroyRef),
				switchMap((res: any) => {
					return this.#authService.authentication(this.#router.routerState.snapshot).pipe(takeUntilDestroyed(this.#destroyRef));
				})
			)
			.subscribe({
				next: () => {
					// Token senza ruoli = parziale: i ruoli arrivano solo con companyId, quindi deve avere
					// la precedenza la scelta company (AppComponent). Navigare qui sull'area porterebbe su
					// /error/forbidden, perché le sue guardie chiedono ruoli che il token non ha ancora.
					if (!this.#authService.userLoggedValue?.Roles?.length) return;

					// authentication() ha autenticato: senza questa navigazione si resta sulla pagina di
					// login da loggati, e il click su "Login with BasicNet" porta su accounts (che, con la
					// sessione già attiva, scarta il returnUrl e fa atterrare su my.basic.net).
					this.#router.navigateByUrl(this.landingRoute(requestedRoute));
				},
				error: (err: any) => {
					// Se il token non arriva, teniamo traccia del perché: goToLogin() sceglie l'ingresso
					// su accounts in base a questo (vedi AuthService.noteAccountsSession).
					if (err?.status !== undefined) this.#authService.noteAccountsSession(err);
				}
			});
	}

	/** Dopo l'autenticazione: la rotta richiesta prima del login, altrimenti l'area dell'utente. */
	private landingRoute(requestedRoute: string | null): string {
		// il controllo resta anche qui perché i browser possono avere in BARequestRoute valori
		// salvati prima che la regola entrasse in AuthService
		if (requestedRoute && !this.#authService.isEntryRoute(requestedRoute)) return requestedRoute;

		return '/' + (this.#authService.isCompany() ? URLS_GUARD.COMPANY : URLS_GUARD.PHOTOGRAPHER);
	}

	goToLogin(): void {
		this.#authService.login();
	}

	requestCredentials(): void {}
}
